import { useEffect, useState, type ReactNode } from "react";
import {
  Building2,
  Coins,
  CreditCard,
  ExternalLink,
  Download,
  FileText,
  IdCard,
  ScrollText,
  Sparkles,
  Truck,
} from "lucide-react";

import { Button } from "../../components/Button/Button";
import { Dialog } from "../../components/Dialog/Dialog";
import { type CaseRow, api } from "../../lib/api";

import styles from "./CaseEditDialog.module.css";

interface CaseEditDialogProps {
  open: boolean;
  caseRow: CaseRow | null;
  mode?: "edit" | "create";
  onClose: () => void;
  onSaved: () => void;
}

const ESTADO_OPTIONS = [
  { value: "Por Enviar", label: "Por Enviar" },
  { value: "Enviado", label: "Enviado" },
  { value: "Por Reenviar", label: "Por Reenviar" },
];

// Únicos códigos aceites pelo EMRP na coluna TipoDocto.
const TIPO_OPTIONS = [
  { value: "FT", label: "FT — Fatura" },
  { value: "PG", label: "PG — Pagamento" },
  { value: "NC", label: "NC — Nota de Crédito" },
  { value: "ND", label: "ND — Nota de Débito" },
  { value: "CR", label: "CR — Correção" },
];

const percentFormatter = new Intl.NumberFormat("pt-PT", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const MOVOP_OPTIONS = [
  { value: "i", label: "i (entrada)" },
  { value: "o", label: "o (saída)" },
];

interface FormState {
  CodProjeto: string;
  MovOp: string;
  candidatura_numero: string;
  NIFDestinatario: string;
  PaisFornecedor: string;
  VATFornecedor: string;
  NomeFornecedor: string;
  TipoDocto: string;
  NumDocto: string;
  DataDocto: string;
  ValorTotal: string;
  ValorImputado: string;
  ValorIncentivo: string;
  IVATotal: string;
  PagtoData: string;
  PagtoRef: string;
  ContratoPublico: boolean;
  IdContratoPublico: string;
  Rubrica: string;
  Descricao: string;
  MovBase: string;
  Estado: CaseRow["Estado"];
  FicheiroUrl?: string;
}

function rowToForm(row: CaseRow | null): FormState {
  return {
    CodProjeto: row?.CodProjeto ?? "",
    MovOp: row?.MovOp ?? "i",
    candidatura_numero: row?.candidatura_numero ?? "",
    NIFDestinatario: row?.NIFDestinatario ?? "",
    PaisFornecedor: row?.PaisFornecedor ?? "",
    VATFornecedor: row?.VATFornecedor ?? "",
    NomeFornecedor: row?.NomeFornecedor ?? "",
    TipoDocto: row?.TipoDocto ?? "",
    NumDocto: row?.NumDocto ?? "",
    DataDocto: row?.DataDocto ?? "",
    ValorTotal: row?.ValorTotal ?? "",
    ValorImputado: row?.ValorImputado ?? "",
    ValorIncentivo: row?.ValorIncentivo ?? row?.ValorImputado ?? "",
    IVATotal: row?.IVATotal ?? "",
    PagtoData: row?.PagtoData ?? "",
    PagtoRef: row?.PagtoRef ?? "",
    ContratoPublico: Boolean(row?.ContratoPublico),
    IdContratoPublico: row?.IdContratoPublico ?? "",
    Rubrica: row?.Rubrica ?? "10",
    Descricao: row?.Descricao ?? "",
    MovBase: row?.MovBase ?? "",
    Estado: row?.Estado ?? "Por Enviar",
    FicheiroUrl: row?.attachments?.[0]?.file ?? "",
  };
}

function nullableNumeric(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const normalised = trimmed.replace(",", ".");
  const num = Number(normalised);
  if (Number.isNaN(num)) return trimmed;
  return normalised;
}

function nullableDate(value: string): string | null {
  return value.trim() === "" ? null : value;
}

export function CaseEditDialog({
  open,
  caseRow,
  mode = "edit",
  onClose,
  onSaved,
}: CaseEditDialogProps) {
  const [form, setForm] = useState<FormState>(() => rowToForm(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiNotice, setAiNotice] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(rowToForm(caseRow));
      setError(null);
      setAiNotice(null);
    }
  }, [open, caseRow]);

  // Nas faturas o valor imputado é calculado: é a parte do incentivo que cabe
  // a esta fatura, em proporção do seu valor total no projeto.
  const isFatura = form.TipoDocto === "FT";
  // Ponderação já gravada; num caso novo ainda não existe.
  const incentivo = Number(caseRow?.ValorIncentivo);
  const percentagem =
    caseRow?.ValorImputado && incentivo > 0
      ? percentFormatter.format(Number(caseRow.ValorImputado) / incentivo)
      : null;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleAiFill() {
    if (!caseRow) return;
    setAiLoading(true);
    setError(null);
    setAiNotice(null);
    try {
      const { fields } = await api.aiExtract(caseRow.id);
      let filled = 0;
      setForm((prev) => {
        const next = { ...prev };
        // NomeFornecedor: o AI pode sobrescrever (as heurísticas falham com frequência)
        if (fields.NomeFornecedor && fields.NomeFornecedor !== prev.NomeFornecedor) {
          next.NomeFornecedor = fields.NomeFornecedor;
          filled += 1;
        }
        // Campos do QR code e do portal: só preencher se estiverem vazios
        for (const key of [
          "VATFornecedor",
          "PaisFornecedor",
          "TipoDocto",
          "NumDocto",
          "DataDocto",
          "ValorTotal",
          "IVATotal",
        ] as const) {
          const value = fields[key];
          if (value && !prev[key]) {
            next[key] = value;
            filled += 1;
          }
        }
        if (!prev.PagtoData && fields.DataDocto) {
          next.PagtoData = fields.DataDocto;
        }
        return next;
      });
      setAiNotice(
        filled > 0
          ? `A IA sugeriu valores para ${filled} campo(s). Reveja antes de guardar.`
          : "Concluído!",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha na extração por IA.");
    } finally {
      setAiLoading(false);
    }
  }

  async function handleMarkForResend() {
    if (!caseRow) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateCase(caseRow.id, { Estado: "Por Reenviar" });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao marcar para reenvio.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    if (mode === "edit" && !caseRow) return;
    setSaving(true);
    setError(null);
    try {
      const payload: Partial<CaseRow> = {
        CodProjeto: form.CodProjeto,
        MovOp: form.MovOp,
        candidatura_numero: form.candidatura_numero,
        NIFDestinatario: form.NIFDestinatario,
        PaisFornecedor: form.PaisFornecedor,
        VATFornecedor: form.VATFornecedor,
        NomeFornecedor: form.NomeFornecedor,
        TipoDocto: form.TipoDocto,
        NumDocto: form.NumDocto,
        DataDocto: nullableDate(form.DataDocto),
        ValorTotal: nullableNumeric(form.ValorTotal),
        ...(isFatura
          ? { ValorIncentivo: nullableNumeric(form.ValorIncentivo) }
          : { ValorImputado: nullableNumeric(form.ValorImputado) }),
        IVATotal: nullableNumeric(form.IVATotal),
        PagtoData: nullableDate(form.PagtoData),
        PagtoRef: form.PagtoRef,
        ContratoPublico: form.ContratoPublico,
        IdContratoPublico: form.IdContratoPublico,
        Rubrica: form.Rubrica,
        Descricao: form.Descricao,
        MovBase: form.MovBase,
        Estado: form.Estado,
      };
      if (mode === "create") {
        await api.createCase(payload);
      } else if (caseRow) {
        await api.updateCase(caseRow.id, payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao guardar as alterações.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      title={
        mode === "create"
          ? "Criar caso"
          : caseRow
            ? `Editar caso MOV${String(caseRow.MovNum).padStart(7, "0")}`
            : "Editar caso"
      }
      onClose={onClose}
      size="lg"
      footer={
        <>
          {mode === "edit" && caseRow && (
            <Button variant="secondary" onClick={handleMarkForResend} disabled={saving}>
              Marcar para reenvio
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || (mode === "edit" && !caseRow)}
          >
            {saving
              ? "A guardar…"
              : mode === "create"
                ? "Criar caso"
                : "Guardar alterações"}
          </Button>
        </>
      }
    >
      {error && <div className={styles.errorBanner}>{error}</div>}
      {aiNotice && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            marginBottom: "12px",
            borderRadius: "8px",
            background: "#eefaf0",
            border: "1px solid #bfe8c8",
            color: "#1c7c3c",
            fontSize: "0.875rem",
          }}
        >
          <Sparkles size={14} />
          {aiNotice}
        </div>
      )}

      {mode === "edit" && caseRow && caseRow.attachments?.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
          <Button variant="secondary" onClick={handleAiFill} disabled={aiLoading || saving}>
            <Sparkles size={14} />
            {aiLoading ? "A analisar a fatura…" : "Preencher com IA"}
          </Button>
        </div>
      )}

      <div className={styles.sections}>
      <Section
        title="Identificação"
        subtitle="Código do projecto, candidatura e estado do caso"
        icon={<IdCard size={16} />}
      >
        <Field label="CodProjeto">
          <input
            className={styles.input}
            value={form.CodProjeto}
            onChange={(e) => update("CodProjeto", e.target.value)}
          />
        </Field>
        <Field label="Nº candidatura">
          <input
            className={styles.input}
            value={form.candidatura_numero}
            onChange={(e) => update("candidatura_numero", e.target.value)}
          />
        </Field>
        <Field label="MovOp">
          <select
            className={styles.input}
            value={form.MovOp}
            onChange={(e) => update("MovOp", e.target.value)}
          >
            {MOVOP_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Estado">
          <select
            className={styles.input}
            value={form.Estado}
            onChange={(e) => update("Estado", e.target.value as CaseRow["Estado"])}
          >
            {ESTADO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Exportações" full>
          {caseRow?.exports?.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {caseRow.exports.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => void api.downloadExport(e.id, e.filename)}
                  style={{ display: "flex", alignItems: "center", gap: "6px", color: "#0066cc", background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: "0.875rem" }}
                >
                  <Download size={13} />
                  {e.filename}
                </button>
              ))}
            </div>
          ) : (
            <span style={{ color: "#666", fontStyle: "italic", fontSize: "0.875rem" }}>
              Ainda não exportado
            </span>
          )}
        </Field>
      </Section>

      <Section
        title="Destinatário"
        subtitle="Entidade que recebe a fatura"
        icon={<Building2 size={16} />}
      >
        <Field label="NIFDestinatário" full>
          <input
            className={styles.input}
            value={form.NIFDestinatario}
            onChange={(e) => update("NIFDestinatario", e.target.value)}
          />
        </Field>
      </Section>

      <Section
        title="Fornecedor"
        subtitle="Quem emitiu o documento"
        icon={<Truck size={16} />}
      >
        <Field label="País">
          <input
            className={styles.input}
            value={form.PaisFornecedor}
            onChange={(e) => update("PaisFornecedor", e.target.value)}
            maxLength={4}
          />
        </Field>
        <Field label="VATFornecedor">
          <input
            className={styles.input}
            value={form.VATFornecedor}
            onChange={(e) => update("VATFornecedor", e.target.value)}
          />
        </Field>
        <Field label="Nome do fornecedor" full>
          <input
            className={styles.input}
            value={form.NomeFornecedor}
            onChange={(e) => update("NomeFornecedor", e.target.value)}
          />
        </Field>
      </Section>

      <Section
        title="Documento"
        subtitle="Identificação e datas do documento original"
        icon={<FileText size={16} />}
      >
        <Field label="Tipo de documento">
          <select
            className={styles.input}
            value={form.TipoDocto}
            onChange={(e) => update("TipoDocto", e.target.value)}
          >
            {!TIPO_OPTIONS.some((o) => o.value === form.TipoDocto) && (
              <option value={form.TipoDocto}>{form.TipoDocto || "—"}</option>
            )}
            {TIPO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Nº de documento">
          <input
            className={styles.input}
            value={form.NumDocto}
            onChange={(e) => update("NumDocto", e.target.value)}
          />
        </Field>
        <Field label="Data do documento">
          <input
            type="date"
            className={styles.input}
            value={form.DataDocto}
            onChange={(e) => update("DataDocto", e.target.value)}
          />
        </Field>
        <Field label="Rubrica">
          <input
            className={styles.input}
            value={form.Rubrica}
            onChange={(e) => update("Rubrica", e.target.value)}
          />
        </Field>
        <Field label="Fatura Original">
          {form.FicheiroUrl ? (
            <a 
              href={form.FicheiroUrl}
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.attachmentButton} 
              style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', color: '#0066cc' }}
            >
              <FileText size={16} />
              Ver Fatura Original
              <ExternalLink size={14} />
            </a>
          ) : (
            <span style={{ color: '#666', fontStyle: 'italic' }}>
              Sem anexo disponível
            </span>
          )}
        </Field>
      </Section>

      <Section
        title="Valores"
        subtitle="Total facturado, imputado e IVA"
        icon={<Coins size={16} />}
      >
        <Field label="Valor total (€)">
          <input
            className={styles.input}
            inputMode="decimal"
            value={form.ValorTotal}
            onChange={(e) => update("ValorTotal", e.target.value)}
          />
        </Field>
        {isFatura && (
          <Field label="Valor do incentivo (€)">
            <input
              className={styles.input}
              inputMode="decimal"
              value={form.ValorIncentivo}
              onChange={(e) => update("ValorIncentivo", e.target.value)}
            />
            <span className={styles.fieldHint}>
              Comum a todas as faturas deste CodProjeto.
            </span>
          </Field>
        )}
        <Field label="Valor imputado (€)">
          <input
            className={styles.input}
            inputMode="decimal"
            value={form.ValorImputado}
            onChange={(e) => update("ValorImputado", e.target.value)}
            disabled={isFatura}
          />
          {isFatura && (
            <span className={styles.fieldHint}>
              {percentagem
                ? `${percentagem} do incentivo (calculado automaticamente).`
                : "Calculado automaticamente a partir do incentivo."}
            </span>
          )}
        </Field>
        <Field label="IVA total (€)">
          <input
            className={styles.input}
            inputMode="decimal"
            value={form.IVATotal}
            onChange={(e) => update("IVATotal", e.target.value)}
          />
        </Field>
      </Section>

      <Section
        title="Pagamento"
        subtitle="Quando e como o documento foi pago"
        icon={<CreditCard size={16} />}
      >
        <Field label="Data de pagamento">
          <input
            type="date"
            className={styles.input}
            value={form.PagtoData}
            onChange={(e) => update("PagtoData", e.target.value)}
          />
        </Field>
        <Field label="Referência">
          <input
            className={styles.input}
            value={form.PagtoRef}
            onChange={(e) => update("PagtoRef", e.target.value)}
          />
        </Field>
      </Section>

      <Section
        title="Contrato público"
        subtitle="Aplicável quando o caso resulta de procedimento concursal"
        icon={<ScrollText size={16} />}
      >
        <Field label="É contrato público?">
          <label className={styles.switch}>
            <input
              type="checkbox"
              checked={form.ContratoPublico}
              onChange={(e) => update("ContratoPublico", e.target.checked)}
            />
            <span>{form.ContratoPublico ? "Sim" : "Não"}</span>
          </label>
        </Field>
        <Field label="Identificador do contrato">
          <input
            className={styles.input}
            value={form.IdContratoPublico}
            onChange={(e) => update("IdContratoPublico", e.target.value)}
            disabled={!form.ContratoPublico}
          />
        </Field>
      </Section>

      <Section
        title="Outros"
        subtitle="Movimento base e descrição livre"
        icon={<Sparkles size={16} />}
      >
        <Field label="MovBase">
          <input
            className={styles.input}
            value={form.MovBase}
            onChange={(e) => update("MovBase", e.target.value)}
          />
        </Field>
        <Field label="Descrição" full>
          <textarea
            className={`${styles.input} ${styles.textarea}`}
            value={form.Descricao}
            onChange={(e) => update("Descricao", e.target.value)}
            rows={3}
          />
        </Field>
      </Section>
      </div>
    </Dialog>
  );
}

interface SectionProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  children: ReactNode;
}

function Section({ title, subtitle, icon, children }: SectionProps) {
  return (
    <section className={styles.section}>
      <header className={styles.sectionHeader}>
        {icon && (
          <span className={styles.sectionIcon} aria-hidden>
            {icon}
          </span>
        )}
        <div>
          <h3 className={styles.sectionTitle}>{title}</h3>
          {subtitle && <div className={styles.sectionSubtitle}>{subtitle}</div>}
        </div>
      </header>
      <div className={styles.grid}>{children}</div>
    </section>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <label className={`${styles.field} ${full ? styles.fieldFull : ""}`}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}
